import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'motion/react';
import { ArrowDown, ArrowLeftRight, ArrowUp, CreditCard, type LucideIcon } from 'lucide-react';
import type { TransactionType } from '@lira/shared';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { BottomSheet } from '@/components/ui';
import { cn } from '@/lib/cn';

const EASE = [0.16, 1, 0.3, 1] as const;

interface TypeOption {
  type: TransactionType | 'card';
  label: string;
  description: string;
  icon: LucideIcon;
  /** classes de cor do ícone (fundo suave + glifo). */
  iconWrap: string;
  disabled?: boolean;
  disabledHint?: string;
}

const OPTIONS: TypeOption[] = [
  {
    type: 'expense',
    label: 'Despesa',
    description: 'Saída de dinheiro de uma conta',
    icon: ArrowDown,
    iconWrap: 'bg-expense-weak text-expense',
  },
  {
    type: 'income',
    label: 'Receita',
    description: 'Entrada de dinheiro em uma conta',
    icon: ArrowUp,
    iconWrap: 'bg-income-weak text-income',
  },
  {
    type: 'card',
    label: 'Despesa no cartão',
    description: 'Compra na fatura do cartão',
    icon: CreditCard,
    iconWrap: 'bg-cards/10 text-cards',
  },
  {
    type: 'transfer',
    label: 'Transferência',
    description: 'Movimento entre suas contas',
    icon: ArrowLeftRight,
    iconWrap: 'bg-balance/10 text-balance',
  },
];

interface TypeSheetProps {
  open: boolean;
  anchor: DOMRect | null;
  onClose: () => void;
  onPick: (type: TransactionType | 'card') => void;
}

function OptionRow({
  opt,
  onPick,
}: {
  opt: TypeOption;
  onPick: (type: TransactionType | 'card') => void;
}) {
  const Icon = opt.icon;
  return (
    <button
      type="button"
      disabled={opt.disabled}
      title={opt.disabled ? opt.disabledHint : undefined}
      onClick={() => !opt.disabled && onPick(opt.type)}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
        opt.disabled ? 'cursor-not-allowed opacity-55' : 'hover:bg-canvas',
      )}
    >
      <span className={cn('grid size-10 shrink-0 place-items-center rounded-full', opt.iconWrap)}>
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="font-semibold text-ink">{opt.label}</span>
          {opt.disabled && (
            <span className="rounded-full bg-canvas px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
              {opt.disabledHint}
            </span>
          )}
        </span>
        <span className="block truncate text-[13px] text-muted">{opt.description}</span>
      </span>
    </button>
  );
}

/** Seletor de tipo de lançamento aberto pelo FAB. Popover no desktop, bottom sheet no mobile. */
export function TypeSheet({ open, anchor, onClose, onPick }: TypeSheetProps) {
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!isDesktop) {
    return (
      <BottomSheet open={open} onClose={onClose} title="Novo lançamento">
        <div className="space-y-1">
          {OPTIONS.map((opt) => (
            <OptionRow key={opt.type} opt={opt} onPick={onPick} />
          ))}
        </div>
      </BottomSheet>
    );
  }

  // Popover ancorado ao FAB (à direita do rail). Fallback centralizado se não houver rect.
  const left = anchor ? anchor.right + 12 : 108;
  const top = anchor ? anchor.top : 96;

  return createPortal(
    <AnimatePresence>
      {open && (
        <m.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12, ease: EASE }}
          onClick={onClose}
          className="fixed inset-0 z-50"
        >
          <m.div
            key="pop"
            role="menu"
            aria-label="Tipo de lançamento"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.16, ease: EASE }}
            style={{ top, left, transformOrigin: 'top left' }}
            className="absolute w-[300px] rounded-2xl border border-border bg-surface p-2 shadow-[var(--shadow-modal)]"
          >
            <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
              Novo lançamento
            </p>
            {OPTIONS.map((opt) => (
              <OptionRow key={opt.type} opt={opt} onPick={onPick} />
            ))}
          </m.div>
        </m.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
